import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import useSupabase from '../../../hooks/useSupabase';
import { useAuth } from '../../../contexts/AuthContext';

const AddTechnicianModal = ({ isOpen, onClose, onTechnicianAdded }) => {
  const { user } = useAuth();
  const { supabase } = useSupabase();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const technicianData = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone: formData.phone.trim() || null,
        email: formData.email.trim().toLowerCase(),
        role: 'technician',
        status: 'active',
        created_by_id: user?.id
      };


      const { data: newTechnician, error } = await supabase
        .from('technicians')
        .insert([technicianData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: ''
      });
      
      // Close modal and notify parent
      onClose();
      onTechnicianAdded?.(newTechnician);
      
      // Show success message
      alert('Techniker erfolgreich hinzugefügt!');
      
    } catch (error) {
      console.error('Error creating technician:', error);
      
      // Handle specific errors
      if (error.code === '23505' && error.message.includes('email')) {
        setErrors({ email: 'Diese E-Mail-Adresse wird bereits verwendet' });
      } else {
        setErrors({ general: `Fehler beim Erstellen des Technikers: ${error.message}` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form and errors when closing
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            Neuen Techniker hinzufügen
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Error Display */}
          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{errors.general}</p>
            </div>
          )}

          {/* First Name */}
          <Input
            label="Vorname *"
            placeholder="Vorname eingeben"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={errors.firstName}
            required
          />

          {/* Last Name */}
          <Input
            label="Nachname *"
            placeholder="Nachname eingeben"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            error={errors.lastName}
            required
          />

          {/* Phone */}
          <Input
            label="Telefonnummer"
            placeholder="Telefonnummer eingeben (optional)"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={errors.phone}
          />

          {/* Email */}
          <Input
            label="E-Mail *"
            type="email"
            placeholder="E-Mail-Adresse eingeben"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email}
            required
          />

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="min-w-[100px]"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                <>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Erstellen
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTechnicianModal;
